/**
 * B.7.e Step 2 — operator uploads firm-supplied source documents that
 * Step 3 will sanitize. Files stay in memory (NOT persisted to Convex
 * file storage) — this is a transient operator workflow, not a Repancy
 * asset.
 *
 * C.3b — each uploaded file is tagged with the destination matter code
 * (drawn from the selected template's mattersJson) and a destination
 * folder (Inputs / Drafts / Final / Audit / Reviewer-Notes). Step 4
 * forwards these to the Function App which uploads under
 * `01-Matters/<matterCode>/<folder>/<name>`.
 */

import { useMemo, useRef, useState } from 'react';
import { useQuery } from 'convex/react';
import { api } from '../../../convex/_generated/api';
import { testIds } from '../../lib/testIds';
import type { WizardState } from './WizardShell';

export type MatterDocFolder =
  | 'Inputs'
  | 'Drafts'
  | 'Final'
  | 'Audit'
  | 'Reviewer-Notes';

export interface UploadedFile {
  name: string;
  body: string;
  documentType: string;
  /** Matter code from the selected template's mattersJson. */
  matterCode: string;
  /** Destination subfolder under `01-Matters/<matterCode>/`. */
  folder: MatterDocFolder;
}

interface Step2Props {
  state: WizardState;
  onBack: () => void;
  onContinue: (files: UploadedFile[]) => void;
}

const FOLDERS: ReadonlyArray<MatterDocFolder> = [
  'Inputs',
  'Drafts',
  'Final',
  'Audit',
  'Reviewer-Notes',
];

function inferDocumentType(name: string): string {
  const lower = name.toLowerCase();
  if (lower.includes('msa')) return 'MSA';
  if (lower.includes('nda')) return 'NDA';
  if (lower.includes('loi')) return 'LOI';
  if (lower.includes('brief')) return 'Brief';
  if (lower.includes('complaint')) return 'Complaint';
  if (lower.includes('motion')) return 'Motion';
  if (lower.includes('memo')) return 'Memo';
  return 'Document';
}

interface MatterOption {
  code: string;
  title: string;
}

function parseMatters(mattersJson: string | undefined): MatterOption[] {
  if (mattersJson === undefined) return [];
  try {
    const parsed = JSON.parse(mattersJson) as Array<{
      code?: unknown;
      title?: unknown;
    }>;
    return parsed
      .filter(
        (m): m is { code: string; title: string } =>
          typeof m.code === 'string' && typeof m.title === 'string',
      )
      .map((m) => ({ code: m.code, title: m.title }));
  } catch {
    return [];
  }
}

export function Step2Upload({ state, onBack, onContinue }: Step2Props) {
  const template = useQuery(
    api.portalProvisioning.getTemplate,
    state.templateId !== null ? { templateId: state.templateId } : 'skip',
  );
  const matters = useMemo(
    () => parseMatters(template?.mattersJson),
    [template?.mattersJson],
  );
  const defaultMatterCode = matters[0]?.code ?? '';

  const [files, setFiles] = useState<UploadedFile[]>(state.uploadedFiles);
  const inputRef = useRef<HTMLInputElement>(null);

  async function handleSelect(fl: FileList | null) {
    if (fl === null) return;
    const next: UploadedFile[] = [];
    for (const f of Array.from(fl)) {
      const body = await f.text();
      next.push({
        name: f.name,
        body,
        documentType: inferDocumentType(f.name),
        matterCode: defaultMatterCode,
        folder: 'Inputs',
      });
    }
    setFiles((existing) => {
      const byName = new Map(existing.map((u) => [u.name, u]));
      for (const n of next) byName.set(n.name, n);
      return [...byName.values()];
    });
  }

  function remove(name: string) {
    setFiles((existing) => existing.filter((u) => u.name !== name));
  }

  function patch(name: string, p: Partial<UploadedFile>) {
    setFiles((existing) =>
      existing.map((u) => (u.name === name ? { ...u, ...p } : u)),
    );
  }

  const allTagged = files.every(
    (f) => f.matterCode.length > 0 && matters.some((m) => m.code === f.matterCode),
  );
  const canContinue = files.length > 0 && allTagged && matters.length > 0;

  return (
    <section className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold mb-2">Upload firm source docs</h2>
        <p className="text-sm text-gray-800 mb-3">
          Drop the markdown / text files you want sanitized into the new
          portal template. Step 3 sends each through the three-layer
          sanitizer (regex → Claude → diff) for your review. Tag each
          upload with the destination matter and folder.
        </p>
        <label
          data-testid={testIds.portalWizard.uploadDropzone}
          className="block border-2 border-dashed border-black/40 px-6 py-10 text-center text-sm bg-white hover:bg-gray-50 cursor-pointer"
          onDrop={(e) => {
            e.preventDefault();
            void handleSelect(e.dataTransfer.files);
          }}
          onDragOver={(e) => e.preventDefault()}
        >
          Drag &amp; drop or click to select files
          <input
            ref={inputRef}
            type="file"
            multiple
            accept=".md,.txt,.json"
            onChange={(e) => void handleSelect(e.target.files)}
            className="hidden"
          />
        </label>
      </div>

      {matters.length === 0 && template !== undefined && (
        <p className="text-sm text-red-700">
          Selected template has no matters defined. Pick a different template
          in Step 1.
        </p>
      )}

      {files.length > 0 && (
        <ul className="border border-black/30 divide-y divide-black/20 bg-white">
          {files.map((f) => (
            <li
              key={f.name}
              data-testid={testIds.portalWizard.uploadedFile(f.name)}
              className="flex flex-wrap items-center justify-between gap-2 px-3 py-2 text-sm"
            >
              <span className="flex-1 min-w-0">
                <span className="font-semibold">{f.name}</span>
                <span className="ml-2 text-xs text-gray-700">
                  {f.documentType} · {f.body.length.toLocaleString()} chars
                </span>
              </span>
              <span className="flex items-center gap-2 text-xs">
                <label className="flex items-center gap-1">
                  Matter
                  <select
                    data-testid={testIds.portalWizard.uploadMatterSelect(f.name)}
                    value={f.matterCode}
                    onChange={(e) =>
                      patch(f.name, { matterCode: e.target.value })
                    }
                    className="border border-black/40 bg-white px-1 py-0.5 text-xs"
                  >
                    {matters.map((m) => (
                      <option key={m.code} value={m.code}>
                        {m.code} — {m.title}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="flex items-center gap-1">
                  Folder
                  <select
                    data-testid={testIds.portalWizard.uploadFolderSelect(f.name)}
                    value={f.folder}
                    onChange={(e) =>
                      patch(f.name, {
                        folder: e.target.value as MatterDocFolder,
                      })
                    }
                    className="border border-black/40 bg-white px-1 py-0.5 text-xs"
                  >
                    {FOLDERS.map((fd) => (
                      <option key={fd} value={fd}>
                        {fd}
                      </option>
                    ))}
                  </select>
                </label>
              </span>
              <button
                type="button"
                onClick={() => remove(f.name)}
                className="text-xs underline hover:no-underline"
              >
                Remove
              </button>
            </li>
          ))}
        </ul>
      )}

      <div className="flex gap-2">
        <button
          type="button"
          data-testid={testIds.portalWizard.step2Back}
          onClick={onBack}
          className="border border-black bg-white px-4 py-2 text-sm hover:bg-gray-100"
        >
          Back
        </button>
        <button
          type="button"
          data-testid={testIds.portalWizard.step2Next}
          disabled={!canContinue}
          onClick={() => onContinue(files)}
          className="border border-black bg-white px-4 py-2 text-sm disabled:opacity-50 hover:bg-gray-100"
        >
          Next: Sanitize
        </button>
      </div>
    </section>
  );
}
